import { useDynamicBundleStore } from 'stores/dynamic-bundle';
import { useIntl } from 'react-intl';
import { useCallback } from 'react';
import { useRootStore } from 'stores/use-root-store';

interface UseGetBundleName {
  bundleName: string;
  hasPetName: boolean;
}

/**
 * Get bundle name from bundles store
 */
export const useGetBundleName = (): ((bundleId?: string) => UseGetBundleName) => {
  const bundles = useDynamicBundleStore((store) => store.bundles);
  const withPetNamesCounter = useDynamicBundleStore((store) => store.withPetNamesCounter);
  const withoutPetNamesCounter = useDynamicBundleStore((store) => store.withoutPetNamesCounter);
  const { name } = useRootStore((store) => store.currentPetProfile);
  const { formatMessage } = useIntl();
  const hasPetName = name !== undefined && name !== '';

  return useCallback(
    (bundleId?: string) => {
      // if bundleId passed it means bundle should exist then find existing name
      if (bundleId) {
        const searchResult = bundles.find((bundle) => bundle.id === bundleId);
        return {
          bundleName:
            searchResult?.name ||
            formatMessage({ id: 'dynamic-bundle:personal-box:default-title' }),
          hasPetName: !!searchResult?.hasPetName,
        };
      }

      // if there was not bundleId passed, check if user has pet name and generate bundle name
      if (hasPetName) {
        return {
          bundleName: formatMessage(
            { id: 'dynamic-bundle:personal-box:title:with-pet' },
            { suffix: withPetNamesCounter > 0 ? `${name} #${withPetNamesCounter}` : `${name}` }
          ),
          hasPetName,
        };
      }

      return {
        bundleName: formatMessage(
          { id: 'dynamic-bundle:personal-box:title' },
          { suffix: withoutPetNamesCounter > 0 ? `#${withoutPetNamesCounter}` : '' }
        ),
        hasPetName,
      };
    },
    [bundles, formatMessage, hasPetName, name, withoutPetNamesCounter, withPetNamesCounter]
  );
};
